import { useState } from 'react';
import { Search } from 'lucide-react';
import { useLanguage } from '../../functions/useLanguage';
import { translate } from '../../functions/i18n';

// ── Marker identifiers for dynamic translation lookups ───────────────────────

interface LabMarker {
  id: string;
  categoryKey: string;
}

const MARKERS: LabMarker[] = [
  { id: 'creatinine', categoryKey: 'kidneyFunction' },
  { id: 'egfr', categoryKey: 'kidneyFunction' },
  { id: 'bun', categoryKey: 'kidneyFunction' },
  { id: 'tacrolimus', categoryKey: 'immunosuppressants' },
  { id: 'ciclosporin', categoryKey: 'immunosuppressants' },
  { id: 'mmf', categoryKey: 'immunosuppressants' },
  { id: 'potassium', categoryKey: 'electrolytes' },
  { id: 'sodium', categoryKey: 'electrolytes' },
  { id: 'bicarbonate', categoryKey: 'electrolytes' },
  { id: 'phosphate', categoryKey: 'electrolytes' },
  { id: 'calcium', categoryKey: 'electrolytes' },
  { id: 'haemoglobin', categoryKey: 'bloodCount' },
  { id: 'wbc', categoryKey: 'bloodCount' },
  { id: 'platelets', categoryKey: 'bloodCount' },
  { id: 'altAst', categoryKey: 'liverFunction' },
  { id: 'uricAcid', categoryKey: 'metabolic' },
  { id: 'hba1c', categoryKey: 'metabolic' },
  { id: 'cmv', categoryKey: 'infections' },
  { id: 'bkv', categoryKey: 'infections' },
  { id: 'upcr', categoryKey: 'kidneyFunction' },
  { id: 'c0d', categoryKey: 'immunosuppressants' },
];

const CATEGORY_KEYS = ['kidneyFunction', 'immunosuppressants', 'electrolytes', 'bloodCount', 'liverFunction', 'metabolic', 'infections'];

// ── Component ─────────────────────────────────────────────────────────────────

export function LabGlossary() {
  const { language } = useLanguage();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const categoryPillBaseClass = 'px-3 py-1 rounded-full text-xs font-semibold transition-colors';
  const categoryPillActiveClass = `${categoryPillBaseClass} bg-primary-brown text-white`;
  const categoryPillInactiveClass = `${categoryPillBaseClass} bg-gray-100 text-text-secondary`;

  const getOptionalTranslation = (key: string): string | null => {
    const value = translate(language, key as any);
    return value === key ? null : value;
  };

  // Helper to get translated marker data
  const getMarkerData = (markerId: string) => ({
    name: translate(language, `guide.labGlossary.marker.${markerId}.name` as any),
    abbr: translate(language, `guide.labGlossary.marker.${markerId}.abbr` as any),
    category: translate(language, `guide.labGlossary.marker.${markerId}.category` as any),
    why: translate(language, `guide.labGlossary.marker.${markerId}.why` as any),
    typical: getOptionalTranslation(`guide.labGlossary.marker.${markerId}.typical`) ?? 'Ranges vary by lab and species. Use your vet lab report as the source of truth.',
    note: getOptionalTranslation(`guide.labGlossary.marker.${markerId}.note`) ?? 'Track trends over time (not just one result) and discuss any changes with your vet.',
  });

  // Helper to get translated category name
  const getCategoryName = (categoryKey: string) => {
    return translate(language, `guide.labGlossary.category.${categoryKey}` as any);
  };

  // Get unique categories for filter
  const categories = CATEGORY_KEYS.map(key => getCategoryName(key));

  // Filter markers based on search and category
  const filtered = MARKERS.filter(m => {
    const data = getMarkerData(m.id);
    const q = query.toLowerCase();
    const matchesSearch = !q || 
      data.name.toLowerCase().includes(q) || 
      data.abbr.toLowerCase().includes(q) || 
      data.category.toLowerCase().includes(q);
    
    const categoryName = getCategoryName(m.categoryKey);
    const matchesCat = !activeCategory || categoryName === activeCategory;
    return matchesSearch && matchesCat;
  });

  const intro = translate(language, 'guide.labGlossary.intro' as any);
  const searchPlaceholder = translate(language, 'guide.labGlossary.search' as any);
  const allLabel = translate(language, 'guide.labGlossary.all' as any);
  const noMarkersMsg = translate(language, 'guide.labGlossary.noMarkersFound' as any);
  const referenceRangeLabel = translate(language, 'guide.labGlossary.referenceRange' as any);

  return (
    <div className="w-full max-w-5xl mx-auto px-1 pb-4">
      {/* Intro */}
      <p className="text-sm text-text-secondary mb-5 leading-relaxed">
        {intro}
      </p>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-highlight-dark/30 bg-gray-50"
        />
      </div>

      {/* Category filter pills */}
      <div className="flex gap-2 flex-wrap mb-5">
        <button
          onClick={() => setActiveCategory(null)}
          className={!activeCategory ? categoryPillActiveClass : categoryPillInactiveClass}
        >
          {allLabel}
        </button>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            className={activeCategory === cat ? categoryPillActiveClass : categoryPillInactiveClass}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Marker list */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">{noMarkersMsg}</p>
        )}
        {filtered.map(marker => {
          const data = getMarkerData(marker.id);
          return (
            <button
              key={marker.id}
              onClick={() => setExpanded(expanded === marker.id ? null : marker.id)}
              className="w-full text-left bg-white border border-gray-100 rounded-2xl px-4 py-3 transition-all active:scale-[0.99]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-bold text-text-primary text-sm">{data.name}</p>
                  <p className="text-xs text-gray-400">{data.abbr} · <span className="text-primary-brown">{data.category}</span></p>
                </div>
                <svg
                  className={
                    expanded === marker.id
                      ? 'w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2 rotate-180'
                      : 'w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ml-2'
                  }
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
                </svg>
              </div>

              {expanded === marker.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 text-left">
                  <p className="text-sm text-text-secondary leading-relaxed">{data.why}</p>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <div className="rounded-xl border border-primary-brown/20 bg-primary-brown/5 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-primary-brown">{referenceRangeLabel}</p>
                      <p className="mt-1 text-xs text-text-secondary leading-relaxed">{data.typical}</p>
                    </div>

                    <div className="rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">Tips</p>
                      <p className="mt-1 text-xs text-text-secondary leading-relaxed">{data.note}</p>
                    </div>
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
